import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { CustomMessageService } from './shared/services/customPriemngMessage.service';
import { firstValueFrom } from 'rxjs';
import { EnqueueMigrationPayload, EnqueueMigrationResponse, GetMigrationStatusResponse, KeycloakMigrationProgressResponse, RevertMigrationPayload, RevertMigrationResponse, StopMigrationPayload, StopMigrationResponse, UpdatePasswordPayload, UpdatePasswordResponse } from 'models/keycloak.model';

const BASE_API_URL = environment.apiURL;

@Injectable( { providedIn: 'root' } )
export class KeycloakService {

	private readonly http = inject( HttpClient );
	private readonly messageService = inject( CustomMessageService );

	public getMigrationStatus = async ( ): Promise<GetMigrationStatusResponse> => {
		try {
			return await firstValueFrom(
				this.http.get<GetMigrationStatusResponse>( `${BASE_API_URL}/v3/admin/keycloak/user-migration/status` )
			);
		} catch ( error ) {
			const defaultErrorMessage = 'Failed to fetch migration status. Please try again.';
			const errorMessage = error?.error?.message || error?.message || defaultErrorMessage;
			this.messageService.error( { sticky: true, message: errorMessage } );
			return {
				showMigrationPopup: false,
			}
		}
	}

	public getMigrationProgress = async ( clientId: string ): Promise<KeycloakMigrationProgressResponse> => {
		try {
			return await firstValueFrom(
				this.http.get<KeycloakMigrationProgressResponse>( `${BASE_API_URL}/v3/admin/keycloak/migration/progress`, { params: { clientId } } )
			);
		} catch ( error ) {
			const defaultErrorMessage = 'Failed to fetch migration progress. Please try again.';
			const errorMessage = error?.error?.message || error?.message || defaultErrorMessage;
			this.messageService.error( { sticky: true, message: errorMessage } );
			throw error;
		}
	}

	public enqueueMigration = async ( payload: EnqueueMigrationPayload ): Promise<EnqueueMigrationResponse> => {
		try {
			return await firstValueFrom(
				this.http.post<EnqueueMigrationResponse>( `${BASE_API_URL}/v3/admin/keycloak/migration/enqueue`, payload )
			);
		} catch ( error ) {
			const defaultErrorMessage = 'Failed to start migration. Please try again.';
			const errorMessage = error?.error?.message || error?.message || defaultErrorMessage;
			this.messageService.error( { sticky: true, message: errorMessage } );
			throw error;
		}
	}

	public stopMigration = async ( payload: StopMigrationPayload ): Promise<StopMigrationResponse> => {
		try {
			return await firstValueFrom(
				this.http.post<StopMigrationResponse>( `${BASE_API_URL}/v3/admin/keycloak/migration/stop`, payload )
			);
		} catch ( error ) {
			const defaultErrorMessage = 'Failed to stop migration. Please try again.';
			const errorMessage = error?.error?.message || error?.message || defaultErrorMessage;
			this.messageService.error( { sticky: true, message: errorMessage } );
			throw error;
		}
	}

	public revertMigration = async ( payload: RevertMigrationPayload ): Promise<RevertMigrationResponse> => {
		try {
			return await firstValueFrom(
				this.http.post<RevertMigrationResponse>( `${BASE_API_URL}/v3/admin/keycloak/user-migration/revert`, payload )
			);
		} catch ( error ) {
			const defaultErrorMessage = 'Failed to revert migration. Please try again.';
			const errorMessage = error?.error?.message || error?.message || defaultErrorMessage;
			this.messageService.error( { sticky: true, message: errorMessage } );
			throw error;
		}
	}

	public updatePasswordAndMigrateToKeycloak = async ( payload: UpdatePasswordPayload ): Promise<UpdatePasswordResponse> => {
		try {
			return await firstValueFrom(
				this.http.post<UpdatePasswordResponse>( `${BASE_API_URL}/v3/admin/keycloak/user-migration/migrate`, payload )
			);
		} catch ( error ) {
			const defaultErrorMessage = 'Failed to reset password. Please try again.';
			const errorMessage = error?.error?.message || error?.message || defaultErrorMessage;
			this.messageService.error( { sticky: true, message: errorMessage } );
			return {
				success: false,
			}
		}
	}
}
